import { z } from "zod";
import { CatalogSupplySourceSchema, PointVectorSchema } from "./catalog.js";
import { OverrideStructuredChangeSchema } from "./jobs.js";
import { ConfidenceSchema, IdSchema, IsoTimestampSchema, NonEmptyStringSchema, NonNegativeIntegerSchema, SourceRefSchema } from "./source.js";
import { ScopeTagsSchema } from "./tags.js";
const ViewStatusSchema = z.enum(["loading", "ready", "blocked", "error"]);
export const ProjectCockpitScopeStatusSchema = z.enum([
    "included",
    "by_others",
    "excluded"
]);
export const ProjectCockpitScopeDisplayStatusSchema = z.enum([
    "included",
    "byOthers",
    "excluded"
]);
export const ProjectCockpitScopeRowSchema = z
    .object({
    id: IdSchema,
    title: NonEmptyStringSchema,
    status: ProjectCockpitScopeStatusSchema,
    tags: ScopeTagsSchema,
    rationale: NonEmptyStringSchema.optional(),
    label: NonEmptyStringSchema.optional(),
    displayStatus: ProjectCockpitScopeDisplayStatusSchema.optional(),
    sourceLinkStatus: z.enum(["available", "not_produced"]).optional(),
    sourceLinkLabel: NonEmptyStringSchema.optional(),
    sourceRefs: z.array(SourceRefSchema).optional(),
    sourceRefCount: NonNegativeIntegerSchema.optional(),
    ruleTraceRefCount: NonNegativeIntegerSchema.optional(),
    ruleTraceRefs: z.array(NonEmptyStringSchema).optional()
})
    .strict();
export const ProjectCockpitInputRowSchema = z
    .object({
    id: IdSchema,
    kind: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    meta: NonEmptyStringSchema,
    status: z.enum(["accepted", "review", "missing", "not_produced"]),
    sourceRefCount: NonNegativeIntegerSchema
})
    .strict();
export const ProjectCockpitApartmentTypeSummarySchema = z
    .object({
    apartmentTypeId: IdSchema,
    label: NonEmptyStringSchema,
    quantity: NonNegativeIntegerSchema,
    beds: NonNegativeIntegerSchema.optional(),
    controllerCount: NonNegativeIntegerSchema,
    deviceCount: NonNegativeIntegerSchema,
    fcuCount: NonNegativeIntegerSchema,
    hasOpenRisk: z.boolean(),
    hasOpenDecision: z.boolean().optional(),
    hasEdgeFeature: z.boolean()
})
    .strict();
export const ProjectCockpitPartialScopeSchema = z
    .object({
    status: z.literal("partial"),
    message: NonEmptyStringSchema,
    completedApartmentTypeIds: z.array(IdSchema),
    failedApartmentTypeIds: z.array(IdSchema),
    stoppedApartmentTypeIds: z.array(IdSchema),
    omittedApartmentTypeIds: z.array(IdSchema),
    failureKinds: z.array(NonEmptyStringSchema)
})
    .strict();
export const ProjectCockpitViewSchema = z
    .object({
    projectId: IdSchema,
    status: ViewStatusSchema,
    activeCatalogReleaseId: IdSchema,
    activeRuleSetId: IdSchema,
    activeSpecSnapshotId: IdSchema.optional(),
    requirementCount: NonNegativeIntegerSchema,
    openRiskCount: NonNegativeIntegerSchema,
    exportReadiness: z.enum(["not_ready", "ready", "blocked"]),
    updatedAt: IsoTimestampSchema,
    approvedRequirementCount: NonNegativeIntegerSchema.optional(),
    openDecisionCount: NonNegativeIntegerSchema.optional(),
    needsUserDecisionCount: NonNegativeIntegerSchema.optional(),
    clientClarificationCount: NonNegativeIntegerSchema.optional(),
    genuineRiskCount: NonNegativeIntegerSchema.optional(),
    assumptionCount: NonNegativeIntegerSchema.optional(),
    clarificationCount: NonNegativeIntegerSchema.optional(),
    warningCount: NonNegativeIntegerSchema.optional(),
    scopeRows: z.array(ProjectCockpitScopeRowSchema).optional(),
    inputRows: z.array(ProjectCockpitInputRowSchema).optional(),
    apartmentTypes: z.array(ProjectCockpitApartmentTypeSummarySchema).optional(),
    partialScope: ProjectCockpitPartialScopeSchema.nullable().optional(),
    dataGaps: z.array(NonEmptyStringSchema).optional(),
    generatedFrom: NonEmptyStringSchema.optional()
})
    .strict();
export const ProjectSummarySupplySourceRollupSchema = z
    .object({
    titan: NonNegativeIntegerSchema,
    fabricated: NonNegativeIntegerSchema,
    thirdParty: NonNegativeIntegerSchema,
    inHouse: NonNegativeIntegerSchema
})
    .strict();
export const ProjectSummarySchema = z
    .object({
    id: IdSchema,
    name: NonEmptyStringSchema,
    client: NonEmptyStringSchema.nullable().optional(),
    location: NonEmptyStringSchema.nullable().optional(),
    siteAddress: NonEmptyStringSchema.nullable().optional(),
    status: z
        .enum([
        "draft",
        "inputs",
        "requirements",
        "specifying",
        "specified",
        "partial",
        "failed"
    ])
        .optional(),
    createdAt: IsoTimestampSchema.optional(),
    updatedAt: IsoTimestampSchema.optional(),
    apartmentTypeCount: NonNegativeIntegerSchema,
    apartmentCount: NonNegativeIntegerSchema,
    controllerCount: NonNegativeIntegerSchema,
    deviceCount: NonNegativeIntegerSchema,
    infraCount: NonNegativeIntegerSchema,
    openDecisionCount: NonNegativeIntegerSchema,
    needsUserDecisionCount: NonNegativeIntegerSchema,
    blockingRiskCount: NonNegativeIntegerSchema,
    assumptionCount: NonNegativeIntegerSchema,
    clientClarificationCount: NonNegativeIntegerSchema,
    bomRowCount: NonNegativeIntegerSchema,
    bomUnitCount: NonNegativeIntegerSchema.optional(),
    supplySourceRollup: ProjectSummarySupplySourceRollupSchema,
    edgePresent: z.boolean(),
    anomalyCount: NonNegativeIntegerSchema,
    exportReadiness: z.enum(["not_ready", "ready", "blocked"])
})
    .strict();
export const ProjectsListSkippedProjectSchema = z
    .object({
    id: IdSchema,
    name: NonEmptyStringSchema,
    reason: NonEmptyStringSchema
})
    .strict();
export const ProjectsListViewSchema = z
    .object({
    projectIds: z.array(IdSchema),
    status: ViewStatusSchema,
    updatedAt: IsoTimestampSchema,
    projects: z.array(ProjectSummarySchema),
    skippedProjects: z.array(ProjectsListSkippedProjectSchema),
    generatedFrom: NonEmptyStringSchema.optional()
})
    .strict();
export const RequirementStudioInputDocumentSchema = z
    .object({
    id: IdSchema,
    kind: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    meta: NonEmptyStringSchema,
    status: z.enum(["parsed", "queued", "missing", "not_produced"]),
    sourceRefCount: NonNegativeIntegerSchema,
    requirementCount: NonNegativeIntegerSchema,
    dataGap: NonEmptyStringSchema.optional()
})
    .strict();
export const RequirementStudioRequirementRowSchema = z
    .object({
    id: IdSchema,
    text: NonEmptyStringSchema,
    kind: NonEmptyStringSchema,
    status: z.enum(["candidate", "approved", "rejected", "superseded"]),
    confidence: ConfidenceSchema,
    sourceRefs: z.array(SourceRefSchema),
    sourceRefCount: NonNegativeIntegerSchema,
    sourceLinkStatus: z.enum(["available", "not_produced"]),
    sourceLinkLabel: NonEmptyStringSchema
})
    .strict();
export const RequirementStudioViewSchema = z
    .object({
    projectId: IdSchema,
    status: ViewStatusSchema,
    requirementIds: z.array(IdSchema),
    conflictRequirementIds: z.array(IdSchema),
    unresolvedRfiIds: z.array(IdSchema),
    selectedRequirementId: IdSchema.optional(),
    updatedAt: IsoTimestampSchema,
    inputDocuments: z.array(RequirementStudioInputDocumentSchema).optional(),
    requirementRows: z.array(RequirementStudioRequirementRowSchema).optional(),
    dataGaps: z.array(NonEmptyStringSchema).optional()
})
    .strict();
export const ApartmentSolutionViewSchema = z
    .object({
    projectId: IdSchema,
    apartmentTypeId: IdSchema,
    specSnapshotId: IdSchema,
    status: ViewStatusSchema,
    controllerIds: z.array(IdSchema),
    deviceIds: z.array(IdSchema),
    mappingIds: z.array(IdSchema),
    riskIds: z.array(IdSchema),
    selectedItemId: IdSchema.optional(),
    updatedAt: IsoTimestampSchema
})
    .strict();
export const SpecMatrixViewSchema = z
    .object({
    projectId: IdSchema,
    specSnapshotId: IdSchema,
    status: ViewStatusSchema,
    apartmentTypeIds: z.array(IdSchema),
    columnKeys: z.array(NonEmptyStringSchema),
    scopeRows: z.array(ProjectCockpitScopeRowSchema).optional(),
    selectedCellRef: NonEmptyStringSchema.optional(),
    updatedAt: IsoTimestampSchema
})
    .strict();
export const DisplaySupplySourceSchema = z.enum([
    "Titan",
    "Fabricated",
    "3rd-party",
    "In-house"
]);
export const DisplayRiskBadgeSchema = z
    .object({
    level: z.enum(["none", "info", "warning", "blocker"]),
    label: NonEmptyStringSchema,
    riskRefs: z.array(IdSchema),
    notes: NonEmptyStringSchema
})
    .strict();
export const DisplayValidationBadgeSchema = z
    .object({
    level: z.enum(["accepted", "review", "error", "info"]),
    label: NonEmptyStringSchema
})
    .strict();
export const WorkspacePointUseSchema = z
    .object({
    kind: NonEmptyStringSchema,
    required: NonNegativeIntegerSchema,
    available: NonNegativeIntegerSchema,
    spare: NonNegativeIntegerSchema,
    reserved: NonNegativeIntegerSchema,
    violations: z.array(NonEmptyStringSchema)
})
    .strict();
export const WorkspaceTakeoffStatSchema = z
    .object({
    key: NonEmptyStringSchema,
    label: NonEmptyStringSchema,
    value: NonNegativeIntegerSchema,
    tone: z.enum(["controller", "heating", "loads", "network", "risk", "neutral"])
})
    .strict();
export const WorkspaceItemSchema = z
    .object({
    id: IdSchema,
    category: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    partNo: NonEmptyStringSchema.optional(),
    role: NonEmptyStringSchema,
    quantity: NonNegativeIntegerSchema,
    unit: NonEmptyStringSchema,
    why: NonEmptyStringSchema,
    how: NonEmptyStringSchema,
    source: DisplaySupplySourceSchema,
    supplySource: CatalogSupplySourceSchema.optional(),
    riskBadge: DisplayRiskBadgeSchema,
    validationBadge: DisplayValidationBadgeSchema,
    points: z.array(WorkspacePointUseSchema),
    detailLines: z.array(NonEmptyStringSchema),
    sourceRefCount: NonNegativeIntegerSchema,
    sourceRefs: z.array(SourceRefSchema).optional(),
    ruleTraceRefs: z.array(NonEmptyStringSchema)
})
    .strict();
export const WorkspaceNoteSchema = z
    .object({
    id: IdSchema,
    kind: z.enum([
        "clarification",
        "exclusion",
        "commissioning",
        "requirement-amendment",
        "risk",
        "assumption"
    ]),
    title: NonEmptyStringSchema,
    body: NonEmptyStringSchema,
    noPriceImpact: z.boolean(),
    linkedItemIds: z.array(IdSchema)
})
    .strict();
export const WorkspacePointLedgerDisplaySchema = z
    .object({
    required: PointVectorSchema,
    available: PointVectorSchema,
    reserved: PointVectorSchema,
    spare: PointVectorSchema,
    violations: z.array(NonEmptyStringSchema),
    rows: z.array(WorkspacePointUseSchema)
})
    .strict();
export const ApartmentSolutionDisplayViewSchema = ApartmentSolutionViewSchema.extend({
    title: NonEmptyStringSchema,
    subtitle: NonEmptyStringSchema,
    apartmentQuantity: NonNegativeIntegerSchema,
    takeoffs: z.array(WorkspaceTakeoffStatSchema),
    items: z.array(WorkspaceItemSchema),
    pointLedger: WorkspacePointLedgerDisplaySchema,
    notes: z.array(WorkspaceNoteSchema),
    dataGaps: z.array(NonEmptyStringSchema),
    generatedFrom: NonEmptyStringSchema
}).strict();
export const SpecMatrixColumnSchema = z
    .object({
    apartmentTypeId: IdSchema,
    label: NonEmptyStringSchema,
    quantity: NonNegativeIntegerSchema,
    beds: NonNegativeIntegerSchema.optional(),
    peerGroupId: NonEmptyStringSchema.optional(),
    peerGroupLabel: NonEmptyStringSchema.optional(),
    anomalyCount: NonNegativeIntegerSchema.optional(),
    anomalies: z
        .array(z
        .object({
        metric: NonEmptyStringSchema,
        label: NonEmptyStringSchema,
        value: NonNegativeIntegerSchema,
        expected: NonNegativeIntegerSchema,
        delta: z.number().int(),
        peerGroupLabel: NonEmptyStringSchema,
        summary: NonEmptyStringSchema
    })
        .strict())
        .optional()
})
    .strict();
export const SpecMatrixCellSchema = z
    .object({
    apartmentTypeId: IdSchema,
    quantity: NonNegativeIntegerSchema,
    source: DisplaySupplySourceSchema.optional(),
    riskLevel: z.enum(["none", "info", "warning", "blocker"]),
    validationLabel: NonEmptyStringSchema,
    isAnomaly: z.boolean().optional(),
    anomalyMetric: NonEmptyStringSchema.optional(),
    anomalyLabel: NonEmptyStringSchema.optional()
})
    .strict();
export const SpecMatrixRowSchema = z
    .object({
    id: IdSchema,
    label: NonEmptyStringSchema,
    category: NonEmptyStringSchema,
    cells: z.array(SpecMatrixCellSchema)
})
    .strict();
export const SpecMatrixGroupSchema = z
    .object({
    id: IdSchema,
    label: NonEmptyStringSchema,
    rows: z.array(SpecMatrixRowSchema)
})
    .strict();
export const SpecMatrixColumnGroupSchema = z
    .object({
    id: NonEmptyStringSchema,
    label: NonEmptyStringSchema,
    description: NonEmptyStringSchema,
    columnKeys: z.array(NonEmptyStringSchema),
    anomalyCount: NonNegativeIntegerSchema
})
    .strict();
export const SpecMatrixDisplayViewSchema = SpecMatrixViewSchema.extend({
    columns: z.array(SpecMatrixColumnSchema),
    columnGroups: z.array(SpecMatrixColumnGroupSchema).optional(),
    groups: z.array(SpecMatrixGroupSchema),
    dataGaps: z.array(NonEmptyStringSchema),
    generatedFrom: NonEmptyStringSchema
}).strict();
export const RiskLedgerViewSchema = z
    .object({
    projectId: IdSchema,
    status: ViewStatusSchema,
    riskIds: z.array(IdSchema),
    blockerCount: NonNegativeIntegerSchema,
    needsUserDecisionCount: NonNegativeIntegerSchema,
    selectedRiskId: IdSchema.optional(),
    updatedAt: IsoTimestampSchema,
    openRiskCount: NonNegativeIntegerSchema.optional(),
    assumptionCount: NonNegativeIntegerSchema.optional(),
    clarificationCount: NonNegativeIntegerSchema.optional(),
    dataGaps: z.array(NonEmptyStringSchema).optional()
})
    .strict();
export const DecisionsHubResolutionKindSchema = z.enum([
    "product_substitution",
    "strategy_input",
    "quantity_or_tier",
    "not_wired"
]);
export const DecisionsHubOptionSchema = z
    .object({
    id: IdSchema,
    label: NonEmptyStringSchema,
    description: NonEmptyStringSchema.optional(),
    engineDefault: z.boolean(),
    resolutionKind: DecisionsHubResolutionKindSchema,
    resolutionStatus: z.enum(["resolvable", "not_wired"]),
    structuredChange: OverrideStructuredChangeSchema.optional(),
    apartmentTypeId: IdSchema.optional(),
    scopeRefId: IdSchema.optional()
})
    .strict();
export const DecisionsHubDecisionSchema = z
    .object({
    id: IdSchema,
    title: NonEmptyStringSchema,
    theme: NonEmptyStringSchema,
    themeLabel: NonEmptyStringSchema,
    what: NonEmptyStringSchema,
    whyItMatters: NonEmptyStringSchema,
    engineDefault: NonEmptyStringSchema,
    resolutionStatus: z.enum(["resolvable", "surfaced_only"]),
    resolutionKind: DecisionsHubResolutionKindSchema,
    resolutionNotWiredReason: NonEmptyStringSchema.optional(),
    riskIds: z.array(IdSchema),
    openDecisionIds: z.array(IdSchema),
    edgeFeatureIds: z.array(IdSchema),
    affectedApartmentTypeIds: z.array(IdSchema),
    affectedApartmentTypeLabels: z.array(NonEmptyStringSchema),
    affectedApartmentTypeCount: NonNegativeIntegerSchema,
    optionCount: NonNegativeIntegerSchema,
    options: z.array(DecisionsHubOptionSchema),
    sourceRefCount: NonNegativeIntegerSchema,
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(NonEmptyStringSchema),
    dataGaps: z.array(NonEmptyStringSchema)
})
    .strict();
export const DecisionsHubGroupSchema = z
    .object({
    id: IdSchema,
    label: NonEmptyStringSchema,
    decisionIds: z.array(IdSchema),
    count: NonNegativeIntegerSchema,
    affectedApartmentTypeCount: NonNegativeIntegerSchema,
    resolvableCount: NonNegativeIntegerSchema,
    surfacedOnlyCount: NonNegativeIntegerSchema
})
    .strict();
export const DecisionsHubViewSchema = z
    .object({
    projectId: IdSchema,
    status: ViewStatusSchema,
    decisionIds: z.array(IdSchema),
    updatedAt: IsoTimestampSchema,
    needsUserDecisionRiskCount: NonNegativeIntegerSchema,
    laneBOpenDecisionCount: NonNegativeIntegerSchema,
    edgeNeedsUserDecisionCount: NonNegativeIntegerSchema,
    groupedDecisionCount: NonNegativeIntegerSchema,
    affectedApartmentTypeCount: NonNegativeIntegerSchema,
    resolvableDecisionCount: NonNegativeIntegerSchema,
    surfacedOnlyDecisionCount: NonNegativeIntegerSchema,
    decisions: z.array(DecisionsHubDecisionSchema),
    groups: z.array(DecisionsHubGroupSchema),
    dataGaps: z.array(NonEmptyStringSchema),
    generatedFrom: NonEmptyStringSchema.optional()
})
    .strict();
const ImpactPreviewScalarSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null()
]);
export const ImpactPreviewChangeRowSchema = z
    .object({
    kind: z.enum(["product_quantity", "validation_status", "risk_count"]),
    subjectRef: NonEmptyStringSchema,
    before: ImpactPreviewScalarSchema,
    after: ImpactPreviewScalarSchema,
    delta: z.number().optional(),
    summary: NonEmptyStringSchema
})
    .strict();
export const ImpactPreviewMoneyDeltaSchema = z
    .object({
    amount: z.number(),
    currency: z.enum(["GBP", "EUR", "USD"]),
    formatted: NonEmptyStringSchema,
    source: NonEmptyStringSchema
})
    .strict();
export const ImpactPreviewProductQuantityDeltaSchema = z
    .object({
    productRef: NonEmptyStringSchema,
    label: NonEmptyStringSchema,
    before: NonNegativeIntegerSchema,
    after: NonNegativeIntegerSchema,
    delta: z.number().int(),
    unitCost: ImpactPreviewMoneyDeltaSchema.optional(),
    costDelta: ImpactPreviewMoneyDeltaSchema.optional(),
    priceKnown: z.boolean()
})
    .strict();
export const ImpactPreviewCapacityDeltaSchema = z
    .object({
    pointKind: z.enum(["ui", "di", "ao", "do", "relay"]),
    requiredBefore: NonNegativeIntegerSchema,
    requiredAfter: NonNegativeIntegerSchema,
    availableBefore: NonNegativeIntegerSchema,
    availableAfter: NonNegativeIntegerSchema,
    spareBefore: z.number().int(),
    spareAfter: z.number().int(),
    violationCountBefore: NonNegativeIntegerSchema,
    violationCountAfter: NonNegativeIntegerSchema
})
    .strict();
export const ImpactPreviewRiskDeltaSchema = z
    .object({
    before: NonNegativeIntegerSchema,
    after: NonNegativeIntegerSchema,
    delta: z.number().int(),
    newlyRaisedRiskIds: z.array(IdSchema),
    clearedRiskIds: z.array(IdSchema)
})
    .strict();
export const ImpactPreviewGuardrailSchema = z
    .object({
    status: z.enum(["passed", "warning", "blocked"]),
    message: NonEmptyStringSchema,
    findingCodes: z.array(NonEmptyStringSchema)
})
    .strict();
export const ImpactPreviewViewSchema = z
    .object({
    projectId: IdSchema,
    impactPreviewId: IdSchema,
    status: ViewStatusSchema,
    affectedRequirementIds: z.array(IdSchema),
    affectedApartmentTypeIds: z.array(IdSchema),
    affectedRiskIds: z.array(IdSchema),
    summary: NonEmptyStringSchema,
    updatedAt: IsoTimestampSchema,
    overrideDraftId: IdSchema.optional(),
    baseSnapshotId: IdSchema.optional(),
    previewSnapshotId: IdSchema.optional(),
    validationStatus: z.enum(["pass", "warning", "fail"]).optional(),
    changeRows: z.array(ImpactPreviewChangeRowSchema).optional(),
    productQuantityDeltas: z
        .array(ImpactPreviewProductQuantityDeltaSchema)
        .optional(),
    costDelta: ImpactPreviewMoneyDeltaSchema.optional(),
    capacityDeltas: z.array(ImpactPreviewCapacityDeltaSchema).optional(),
    riskDelta: ImpactPreviewRiskDeltaSchema.optional(),
    guardrail: ImpactPreviewGuardrailSchema.optional()
})
    .strict();
export const MappingCommissioningConnectionTypeSchema = z.enum([
    "direct_wired",
    "network_pairing",
    "central_io",
    "not_terminated_spare",
    "physical_termination"
]);
export const MappingCommissioningRowKindSchema = z.enum([
    "device_mapping",
    "physical_mapping"
]);
export const MappingCommissioningScheduleRowSchema = z
    .object({
    id: IdSchema,
    sourceMappingId: IdSchema,
    apartmentTypeId: IdSchema,
    apartmentTypeLabel: NonEmptyStringSchema,
    rowKind: MappingCommissioningRowKindSchema,
    connectionType: MappingCommissioningConnectionTypeSchema,
    connectionLabel: NonEmptyStringSchema,
    deviceRef: IdSchema,
    deviceLabel: NonEmptyStringSchema,
    controllerRef: IdSchema.optional(),
    controllerLabel: NonEmptyStringSchema.optional(),
    terminationRef: IdSchema.optional(),
    terminationLabel: NonEmptyStringSchema.optional(),
    roomLabel: NonEmptyStringSchema,
    zoneLabel: NonEmptyStringSchema.optional(),
    cableImplication: NonEmptyStringSchema.optional(),
    commissioningNote: NonEmptyStringSchema,
    riskRefs: z.array(IdSchema),
    sourceRefCount: NonNegativeIntegerSchema,
    sourceRefs: z.array(SourceRefSchema).optional(),
    ruleTraceRefs: z.array(NonEmptyStringSchema),
    isSpare: z.boolean()
})
    .strict();
export const MappingCommissioningTaskRowSchema = z
    .object({
    id: IdSchema,
    apartmentTypeId: IdSchema,
    apartmentTypeLabel: NonEmptyStringSchema,
    title: NonEmptyStringSchema,
    description: NonEmptyStringSchema,
    status: z.enum(["planned", "ready", "blocked", "done"]),
    deviceLabels: z.array(NonEmptyStringSchema),
    riskRefs: z.array(IdSchema),
    sourceRefCount: NonNegativeIntegerSchema,
    sourceRefs: z.array(SourceRefSchema).optional(),
    ruleTraceRefs: z.array(NonEmptyStringSchema)
})
    .strict();
export const MappingCommissioningConnectionBreakdownSchema = z
    .object({
    directWired: NonNegativeIntegerSchema,
    networkPairing: NonNegativeIntegerSchema,
    centralIo: NonNegativeIntegerSchema,
    notTerminatedSpare: NonNegativeIntegerSchema,
    physicalTermination: NonNegativeIntegerSchema
})
    .strict();
export const MappingCommissioningStatsSchema = z
    .object({
    scheduleRowCount: NonNegativeIntegerSchema,
    deviceMappingCount: NonNegativeIntegerSchema,
    physicalMappingCount: NonNegativeIntegerSchema,
    sparePointCount: NonNegativeIntegerSchema,
    commissioningTaskCount: NonNegativeIntegerSchema,
    deviceConnectionBreakdown: MappingCommissioningConnectionBreakdownSchema,
    scheduleConnectionBreakdown: MappingCommissioningConnectionBreakdownSchema
})
    .strict();
export const MappingCommissioningApartmentSummarySchema = z
    .object({
    apartmentTypeId: IdSchema,
    apartmentTypeLabel: NonEmptyStringSchema,
    scheduleRowCount: NonNegativeIntegerSchema,
    deviceMappingCount: NonNegativeIntegerSchema,
    physicalMappingCount: NonNegativeIntegerSchema,
    sparePointCount: NonNegativeIntegerSchema
})
    .strict();
export const MappingCommissioningControllerGroupSchema = z
    .object({
    id: NonEmptyStringSchema,
    apartmentTypeId: IdSchema,
    apartmentTypeLabel: NonEmptyStringSchema,
    controllerRef: IdSchema,
    controllerLabel: NonEmptyStringSchema,
    scheduleRowCount: NonNegativeIntegerSchema,
    deviceRowCount: NonNegativeIntegerSchema,
    physicalRowCount: NonNegativeIntegerSchema,
    sparePointCount: NonNegativeIntegerSchema,
    connectionBreakdown: MappingCommissioningConnectionBreakdownSchema
})
    .strict();
export const MappingCommissioningViewSchema = z
    .object({
    projectId: IdSchema,
    status: ViewStatusSchema,
    scheduleRowIds: z.array(IdSchema),
    commissioningTaskIds: z.array(IdSchema),
    updatedAt: IsoTimestampSchema,
    stats: MappingCommissioningStatsSchema,
    rows: z.array(MappingCommissioningScheduleRowSchema),
    commissioningTasks: z.array(MappingCommissioningTaskRowSchema),
    apartmentSummaries: z.array(MappingCommissioningApartmentSummarySchema),
    controllerGroups: z.array(MappingCommissioningControllerGroupSchema),
    dataGaps: z.array(NonEmptyStringSchema),
    generatedFrom: NonEmptyStringSchema.optional()
})
    .strict();
export const ExportPreviewViewSchema = z
    .object({
    projectId: IdSchema,
    exportBundleId: IdSchema,
    status: ViewStatusSchema,
    artifactIds: z.array(IdSchema),
    blockingRiskIds: z.array(IdSchema),
    previewUrl: z.string().url().optional(),
    updatedAt: IsoTimestampSchema
})
    .strict();
//# sourceMappingURL=views.js.map